import { AccountResourceDto, Features } from '@/munio/api/data/AccountResourceDto'
import { UserBasicResourceDto, ZoneBasicResourceDto } from '@/munio/api/data'
import { CollectionResponse, ResourceResponse } from '@/munio/api/data/common'
import { Api } from '@/munio/api/base'

export type UserRole = {
  id: number
  role: string
  user: UserBasicResourceDto
}

export type AccountInfo = {
  logo?: File
  background?: File
  supportEmail?: string
  supportPhone?: string
  salesEmail?: string
  salesPhone?: string
}

export type UpdatableAccount = {
  name: string
  expiresAt: string
  features: string[]
}

class Account extends Api<{ id: number }> {
  constructor(id: number) {
    super({ id })
  }

  get() {
    return this.$get<ResourceResponse<AccountResourceDto>>(this.$route('api.web.munio.account.show'))
  }

  update(name: string, state: string, expiresAt: string | undefined, features: Features) {
    return this.$put(this.$route('api.web.munio.account.update'), {
      name,
      state: state,
      expiresAt: expiresAt,
      features,
    })
  }

  updateInfo(info: AccountInfo) {
    const form = new FormData()

    Object.entries(info).forEach(([key, value]) => {
      if (!value) {
        return
      }

      if (value instanceof File) {
        form.append(key, value, value.name)
      } else {
        form.append(key, value)
      }
    })

    return this.$post<ResourceResponse<AccountResourceDto>>(this.$route('api.web.munio.account.update.info'), form)
  }

  delete() {
    return this.$delete(this.$route('api.web.munio.account.delete'))
  }

  get gates() {
    return new Gates(this.params)
  }

  get roles() {
    return new Roles(this.params)
  }
}

class Gates extends Api<{ id: number }> {
  get() {
    return this.$get<CollectionResponse<ZoneBasicResourceDto>>(this.$route('api.web.munio.account.gates'))
  }

  create(name: string) {
    return this.$post(this.$route('api.web.munio.account.gate.create'), { name: name })
  }

  delete(id: number) {
    return this.$delete(this.$route('api.web.munio.account.gate.delete', { gate: id }))
  }
}

class Roles extends Api<{ id: number }> {
  get() {
    return this.$get<CollectionResponse<UserRole>>(this.$route('api.web.munio.account.users'))
  }

  create(user: any, role: string) {
    return this.$post<ResourceResponse<UserRole>>(this.$route('api.web.munio.account.users.create'), {
      user,
      role,
    })
  }

  delete(userId: number) {
    return this.$delete<CollectionResponse<UserRole>>(
      this.$route('api.web.munio.account.users.delete', { user: userId }),
    )
  }
}

export default Account
